import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

import {
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";

import {
  createBuggyCount,
  getBuggyCountData,
  resetBuggyCounts,
  approveBuggyCount,
  updateBuggyCount,
} from "api/inventory";
import { getProductsWithAttributes } from "api/products";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

import { Spacer } from "components/Custom";
import { TextInput } from "components/Form";
import {
  FormattedTextField,
  TextInputWithKeyboard,
} from "components/FormElements";
import PageTitle from "components/PageTitle";
import Widget from "components/Widget";

import { QrCode2, SnippetFolder, TextSnippet, X as Clear } from "icons";
import { separateQtyFromValue } from "utils/string";

import useStyles from "./styles";

const initialParams = {
  number: "",
  type: "Other",
  barcode: "",
  product: "",
  product_sku: "",
  count_group_id: "",
  quantity: "",
};

export default function CycleCounter() {
  const [t] = useTranslation();
  const barcodeEl = useRef(null);
  const skuEl = useRef(null);
  const numberEl = useRef(null);
  const quantityEl = useRef(null);
  const layoutDispatch = useLayoutDispatch();
  const [params, setParams] = useState(initialParams);
  const [countGroups, setCountGroups] = useState([]);
  const [barcodeNotFound, setBarcodeNotFound] = useState(false);
  const [showQtyInput] = useState(false);
  const [resetBuggyID, setResetBuggyID] = useState(null);
  const [approveBuggyID, setApproveBuggyID] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updateSKU, setUpdateSKU] = useState(null);
  const [updateQty, setUpdateQty] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);

  const fetchBuggyData = useCallback((value, { clearOnEmpty = false } = {}) => {
    getBuggyCountData({
      params: value
        ? { buggy: value, count_type: "location" }
        : { count_type: "location" },
      responseSetter: (res) => {
        const { results = [] } = res;

        if (!results.length) {
          if (clearOnEmpty) {
            setParams({ ...initialParams });
          } else {
            setParams((prev) => ({
              ...prev,
              count_group_id: "",
            }));
          }
          setCountGroups([]);
          return;
        }

        setParams((prev) => {
          const next = {
            ...prev,
          };

          if (typeof value === "string") {
            next.number = value;
          }

          const hasSelectedGroup = results.some(
            (group) => group.id === prev.count_group_id,
          );

          if (!hasSelectedGroup) {
            next.count_group_id = "";
          }

          setIsCompleted(
            results.find((group) => group.id === next.count_group_id)
              ?.is_completed || false,
          );

          if (!next.type && results[0]?.type) {
            next.type = results[0].type;
          }

          return next;
        });

        setCountGroups(results);
      },
    });
  }, []);

  const debouncedFetchBuggyData = useMemo(
    () =>
      debounce((value) => {
        fetchBuggyData(value);
      }, 500),
    [fetchBuggyData],
  );

  const handleChangeFilterInput = (prop) => (e) => {
    const { value } = e.target;

    setParams((prev) => ({
      ...prev,
      [prop]: value,
      ...(prop === "number" ? { count_group_id: "" } : {}),
    }));

    if (prop === "number") {
      debouncedFetchBuggyData(value);
    }
  };
  const classes = useStyles();

  useEffect(() => {
    fetchBuggyData("", { clearOnEmpty: true });
  }, [fetchBuggyData]);

  useEffect(
    () => () => {
      debouncedFetchBuggyData.cancel();
    },
    [debouncedFetchBuggyData],
  );

  useEffect(() => {
    if (params.count_group_id) {
      barcodeEl.current.focus();
    }
  }, [params.count_group_id]);

  useEffect(() => {
    if (params.type) {
      numberEl.current.focus();
    }
  }, [params.type]);

  const handleClickListItem = (group) => {
    const { id, number, type } = group;
    setParams((prev) => ({ ...prev, count_group_id: id, type, number }));
    setIsCompleted(group.is_completed || false);
  };

  const handleReset = useCallback(() => {
    setParams({
      ...params,
      barcode: "",
      product: "",
      product_sku: "",
      quantity: "",
    });
    setBarcodeNotFound(false);
    barcodeEl.current.focus();
  }, [params]);

  const handleSearchBarcode = () => {
    const { value, qty } = separateQtyFromValue(params.barcode);
    setParams({ ...params, barcode: value, product_sku: "" });
    getProductsWithAttributes({
      params: {
        value_iexact: value,
        redis_cache: true,
        count_type: "location",
      },
      responseSetter: (res) => {
        const { results } = res;
        if (results.length === 1) {
          setParams({
            ...params,
            barcode: value,
            product: results[0].product,
            product_sku: results[0].product_sku,
            quantity: qty || "",
          });
          setBarcodeNotFound(false);
          quantityEl.current.focus();
        } else {
          setBarcodeNotFound(true);
          popupNotification({
            dispatch: layoutDispatch,
            message: t("unknown_barcode"),
            afterCloseFunction: () => {
              skuEl.current.focus();
            },
          });
        }
      },
    });
  };

  const handleSubmitForm = useCallback(() => {
    const { count_group_id, barcode, product, product_sku, quantity } = params;
    const product_quantity = showQtyInput ? parseInt(quantity) : 1;
    if (product_quantity < 1 || product_quantity > 1000) {
      return popupNotification({
        dispatch: layoutDispatch,
        message: "Quantity must be between 1 and 1000.",
      });
    }

    createBuggyCount({
      body: {
        count_group_id,
        barcode,
        product,
        product_sku,
        quantity: product_quantity,
        count_type: "location",
      },
      responseSetter: (res) => {
        if (res.success) {
          handleReset();
          popupNotification({
            dispatch: layoutDispatch,
            message: t("successful_operation"),
            status: "success",
            afterCloseFunction: () => barcodeEl.current.focus(),
          });
          fetchBuggyData(params.number);
        } else {
          popupNotification({
            dispatch: layoutDispatch,
            message: res.message,
            status: "error",
            afterCloseFunction: () => {
              setParams({
                ...params,
                product_sku: "",
              });
            },
          });
        }
      },
    });
  }, [fetchBuggyData, handleReset, layoutDispatch, params, t, showQtyInput]);

  useEffect(() => {
    if (params.product && params.product_sku && !showQtyInput) {
      handleSubmitForm();
    }
  }, [params.product, params.product_sku, showQtyInput, handleSubmitForm]);

  const handleKeyPress = ({ key }) => {
    if (key !== "Enter") {
      return;
    }
    const findGroup = countGroups.find(
      (group) => group.type === params.type && group.number === params.number,
    );
    if (findGroup) {
      handleClickListItem(findGroup);
    }
  };

  const handleQuantityChange = ({ target: { value } }) => {
    setParams({
      ...params,
      quantity: /^\d+$/.test(value) ? parseInt(value) : "",
    });
  };

  const handleSaveQty = () => {
    updateBuggyCount({
      body: {
        count_group_id: params.count_group_id,
        product_sku: updateSKU,
        quantity: parseInt(updateQty),
      },
      responseSetter: (res) => {
        if (res.success) {
          popupNotification({
            dispatch: layoutDispatch,
            message: t("successful_operation"),
            status: "success",
          });
          fetchBuggyData(params.number);
        } else {
          popupNotification({
            dispatch: layoutDispatch,
            message: res.message,
            status: "error",
          });
        }
      },
    });
    setUpdateSKU(null);
    setUpdateQty("");
  };

  return (
    <>
      <PageTitle title="Cycle Count" />
      <Widget>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <TextInput
              fullWidth
              inputRef={numberEl}
              variant={"outlined"}
              id={"buggy"}
              label={t("Location")}
              value={params.number}
              onChange={handleChangeFilterInput("number")}
              onKeyPress={handleKeyPress}
              disabled={!!params.count_group_id}
              LeftIcon={SnippetFolder}
              hasBorder
              InputProps={{
                endAdornment: params.count_group_id && (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => setResetBuggyID(params.count_group_id)}
                      edge="end"
                    >
                      Reset
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {countGroups.length && !params.count_group_id ? (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <List>
                {countGroups.map((group) => (
                  <ListItem
                    key={group.id}
                    onClick={() => handleClickListItem(group)}
                    className={classes.listItem}
                  >
                    <ListItemText
                      primary={
                        group.number + " (" + group.total_qty.toString() + ")"
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          ) : null}
          {params.count_group_id ? (
            <>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <TextInputWithKeyboard
                  fullWidth
                  id={"barcode"}
                  variant={"outlined"}
                  inputRef={barcodeEl}
                  label={t("barcode")}
                  value={params.barcode}
                  onChange={handleChangeFilterInput("barcode")}
                  onKeyPress={({ key }) =>
                    key === "Enter" ? handleSearchBarcode() : null
                  }
                  disabled={!!params.product_sku || barcodeNotFound}
                  LeftIcon={QrCode2}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setParams({
                              ...params,
                              barcode: "",
                              product_sku: "",
                            });
                            setBarcodeNotFound(false);
                          }}
                          edge="end"
                        >
                          {params.barcode && params.barcode.length > 0 && (
                            <Clear />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  hasBorder
                />
              </Grid>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                style={{ display: !barcodeNotFound ? "none" : "block" }}
              >
                <FormattedTextField
                  fullWidth
                  inputRef={skuEl}
                  variant={"outlined"}
                  id={"product_sku"}
                  label={t("product_sku")}
                  value={params.product_sku}
                  onChange={handleChangeFilterInput("product_sku")}
                  onKeyPress={({ key }) =>
                    key === "Enter"
                      ? showQtyInput
                        ? quantityEl.current.focus()
                        : handleSubmitForm()
                      : null
                  }
                  LeftIcon={QrCode2}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setParams({
                              ...params,
                              product_sku: "",
                            });
                          }}
                          edge="end"
                        >
                          {params.product_sku &&
                            params.product_sku.length > 0 && <Clear />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  isProductSKU
                  hasBorder
                />
              </Grid>
              {showQtyInput && (
                <Grid item xs={12}>
                  <TextInputWithKeyboard
                    inputRef={quantityEl}
                    type="number"
                    size="medium"
                    name="quantity"
                    inputMode="numeric"
                    label={t("quantity")}
                    variant="outlined"
                    value={params.quantity}
                    fullWidth
                    onChange={handleQuantityChange}
                    onKeyPress={({ key }) =>
                      key === "Enter" ? handleSubmitForm() : null
                    }
                    LeftIcon={TextSnippet}
                    hasBorder
                  />
                </Grid>
              )}
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {params.count_group_id &&
                countGroups.length &&
                countGroups.find((g) => g.id === params.count_group_id)
                  ?.total_qty ? (
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginTop: 10, marginBottom: 10 }}
                  >
                    <div
                      style={{
                        textAlign: "left",
                        marginBottom: 0,
                        marginLeft: 10,
                      }}
                    >
                      Total (
                      {countGroups.find((g) => g.id === params.count_group_id)
                        ?.total_qty || 0}
                      )
                    </div>
                    <div>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() =>
                          setApproveBuggyID(params.count_group_id)
                        }
                        edge="end"
                        style={{ marginRight: 1 }}
                        disabled={isCompleted}
                      >
                        Approve
                      </Button>
                    </div>
                  </Grid>
                ) : null}
                {params.count_group_id &&
                  countGroups
                    .filter((group) => group.number === params.count_group_id)
                    .map((group) =>
                      Object.entries(group?.products || {}).map(
                        ([sku, qty]) => (
                          <div
                            key={sku}
                            onClick={() => {
                              setUpdateSKU(sku);
                              setUpdateQty(qty);
                            }}
                            style={{
                              backgroundColor:
                                updateSKU === sku ? "#d1eaff" : "#f0f0f0",
                              margin: 5,
                              padding: 5,
                              paddingLeft: 10,
                              paddingRight: 10,
                              borderRadius: 4,
                              cursor: "pointer",
                              transition: "background 0.2s",
                            }}
                          >
                            <b>{sku}</b>{" "}
                            <span style={{ float: "right" }}>{qty}</span>
                          </div>
                        ),
                      ),
                    )}
              </Grid>
            </>
          ) : null}
        </Grid>
      </Widget>
      <Spacer height="2rem" />
      {approveBuggyID && (
        <Dialog
          open={true}
          onClose={() => setApproveBuggyID(null)}
          aria-labelledby="confirm-dialog"
        >
          <DialogTitle id="confirm-dialog">
            {params.count_group_id} Approve
          </DialogTitle>
          <DialogContent>
            All existing inventory in <b>{params.number}</b> will be
            transferred to LOC-LOST, then new transfers and adjustments will be
            generated based on the counted quantities. Are you sure?
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={isLoading}
              onClick={() => setApproveBuggyID(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const buggyId = approveBuggyID;

                setIsLoading(true);

                if (!buggyId) {
                  setIsLoading(false);
                  return;
                }

                approveBuggyCount({
                  body: { buggy: buggyId, count_type: "location" },
                  responseSetter: (res) => {
                    setIsLoading(false);
                    setApproveBuggyID(null);
                    if (res?.success) {
                      popupNotification({
                        dispatch: layoutDispatch,
                        message: "Successful operation",
                        status: "success",
                      });
                      fetchBuggyData(params.number);
                    } else {
                      popupNotification({
                        dispatch: layoutDispatch,
                        message: "Operation failed",
                        status: "error",
                      });
                    }
                  },
                });
              }}
              color="primary"
              disabled={isLoading}
            >
              {isLoading && <CircularProgress size={20} />}
              Approve
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {resetBuggyID && (
        <Dialog
          open={true}
          onClose={() => setResetBuggyID(null)}
          aria-labelledby="confirm-dialog"
        >
          <DialogTitle id="confirm-dialog">
            {params.count_group_id} Reset
          </DialogTitle>
          <DialogContent>
            <b>{params.count_group_id}</b> items will be reset. Are you sure?
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={isLoading}
              onClick={() => setResetBuggyID(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const buggyId = resetBuggyID;

                setIsLoading(true);

                if (!buggyId) {
                  setIsLoading(false);
                  return;
                }

                resetBuggyCounts({
                  params: { buggy: buggyId, count_type: "location" },
                  responseSetter: (res) => {
                    setResetBuggyID(null);
                    if (res?.success) {
                      setIsLoading(false);
                      popupNotification({
                        dispatch: layoutDispatch,
                        message: "Successful operation",
                        status: "success",
                      });
                      fetchBuggyData(params.number);
                    }
                  },
                });
              }}
              color="primary"
              disabled={isLoading}
            >
              {isLoading && <CircularProgress size={20} />}
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Dialog for updating SKU quantity */}
      <Dialog
        open={!!updateSKU}
        onClose={() => setUpdateSKU(null)}
        aria-labelledby="update-qty-dialog"
      >
        <DialogTitle id="update-qty-dialog">
          Update Quantity for <b>{updateSKU}</b>
        </DialogTitle>
        <DialogContent>
          <TextInput
            label="Quantity"
            type="number"
            value={updateQty}
            onChange={(e) => setUpdateQty(e.target.value)}
            fullWidth
            autoFocus
            hasBorder
            LeftIcon={TextSnippet}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateSKU(null)} variant="contained">
            Cancel
          </Button>
          <Button
            onClick={handleSaveQty}
            color="primary"
            variant="contained"
            disabled={!updateQty || isNaN(updateQty)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
